import { MenuService } from '@/layout/service/menu.service';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { AppMenuItem, PermissionRef } from '@/core/permissions/permission.model';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { CONFIGURACOES_ROUTES } from '@/features/configuracoes/configuracoes.constants';
import { CONFIGURACOES_USUARIO_ROUTES } from '@/features/configuracoes/usuario/configuracoes-usuario.constants';

@Component({
    selector: 'app-configuracoes-content',
    standalone: true,
    imports: [RouterModule],
    template: `
        <main class="w-full">
            <div>
                <router-outlet></router-outlet>
            </div>
        </main>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush
})
/** Shell das Configurações do Sistema: registra o menu lateral (Configurações) e hospeda o `router-outlet`. */
export class ConfiguracoesContent implements OnInit {
    private menuService = inject(MenuService);

    // Menu configuration
    menu: AppMenuItem[] = [];

    constructor() {
        const C = CONFIGURACOES_ROUTES;
        const U = CONFIGURACOES_USUARIO_ROUTES;
        const raiz = '/' + C.ROOT;
        this.menu = [
            {
                label: 'Configurações',
                items: [
                    {
                        label: 'Gerais',
                        expanded: true,
                        icon: 'pi pi-fw pi-cog',
                        items: [
                            {
                                label: 'Email',
                                icon: 'pi pi-fw pi-envelope',
                                routerLink: [raiz, C.GERAIS.ROOT, C.GERAIS.EMAIL],
                                data: { breadcrumb: 'E-Mail' },
                                permission: { modulo: 'configuracoes', funcionalidade: 'email', somenteCfm: true } as PermissionRef
                            },
                            {
                                label: 'Serviços',
                                icon: 'pi pi-fw pi-th-large',
                                routerLink: [raiz, C.GERAIS.ROOT, C.GERAIS.SERVICOS],
                                data: { breadcrumb: 'Serviços' },
                                permission: { modulo: 'configuracoes', funcionalidade: 'servicos' } as PermissionRef
                            },
                            { label: 'Módulos', icon: 'pi pi-fw pi-th-large', routerLink: [raiz, C.GERAIS.ROOT, C.GERAIS.MODULOS], data: { breadcrumb: 'Módulos' }, permission: { modulo: 'configuracoes', funcionalidade: 'modulos' } as PermissionRef }
                        ]
                    },
                    {
                        label: 'Arrecadação',
                        expanded: true,
                        icon: 'pi pi-fw pi-wallet',
                        items: [
                            {
                                label: 'Anuidades',
                                icon: 'pi pi-fw pi-calendar',
                                routerLink: [raiz, C.ARRECADACAO.ROOT, C.ARRECADACAO.ANUIDADES],
                                data: { breadcrumb: 'Anuidades' },
                                permission: { modulo: 'configuracoes', funcionalidade: 'anuidades' } as PermissionRef
                            },
                            {
                                label: 'Bancos, contas e convênios',
                                icon: 'pi pi-fw pi-building',
                                routerLink: [raiz, C.ARRECADACAO.ROOT, C.ARRECADACAO.BANCOS],
                                data: { breadcrumb: 'Bancos, contas e convênios' },
                                permission: { modulo: 'configuracoes', funcionalidade: 'bancos' } as PermissionRef
                            },
                            { label: 'Contabilidade', icon: 'pi pi-fw pi-book', routerLink: [raiz, C.CONTABILIDADE], data: { breadcrumb: 'Contabilidade' }, permission: { modulo: 'configuracoes', funcionalidade: 'contabilidade' } as PermissionRef },
                            { label: 'Refis', icon: 'pi pi-fw pi-percentage', routerLink: [raiz, C.REFIS], data: { breadcrumb: 'Refis' }, permission: { modulo: 'configuracoes', funcionalidade: 'refis' } as PermissionRef },
                            { label: 'Indicadores', icon: 'pi pi-fw pi-chart-line', routerLink: [raiz, C.INDICADORES], data: { breadcrumb: 'Indicadores' }, permission: { modulo: 'configuracoes', funcionalidade: 'indicadores' } as PermissionRef }
                        ]
                    },
                    {
                        label: 'Registro',
                        expanded: true,
                        icon: 'pi pi-fw pi-list',
                        items: [
                            {
                                label: 'Estruturas',
                                icon: 'pi pi-fw pi-sitemap',
                                routerLink: [raiz, C.REGISTRO.ROOT, C.REGISTRO.ESTRUTURAS],
                                data: { breadcrumb: 'Estruturas' },
                                permission: { modulo: 'configuracoes', funcionalidade: 'estruturas' } as PermissionRef
                            },
                            {
                                label: 'Funcionários',
                                icon: 'pi pi-fw pi-id-card',
                                routerLink: [raiz, C.REGISTRO.ROOT, C.REGISTRO.FUNCIONARIOS],
                                data: { breadcrumb: 'Funcionários' },
                                permission: { modulo: 'configuracoes', funcionalidade: 'funcionarios' } as PermissionRef
                            }
                        ]
                    },
                    {
                        label: 'Usuários',
                        expanded: true,
                        icon: 'pi pi-fw pi-users',
                        items: [
                            { label: 'Cadastro', icon: 'pi pi-fw pi-user-plus', routerLink: [raiz, U.ROOT, U.CADASTRO], data: { breadcrumb: 'Cadastro' }, permission: { modulo: 'configuracoes', funcionalidade: 'usuarios' } as PermissionRef },
                            {
                                label: 'Motivo de Afastamento',
                                icon: 'pi pi-fw pi-user-minus',
                                routerLink: [raiz, U.ROOT, U.MOTIVO_AFASTAMENTO],
                                data: { breadcrumb: 'Motivo de Afastamento' },
                                permission: { modulo: 'configuracoes', funcionalidade: 'motivo-afastamento', somenteCfm: true } as PermissionRef
                            },
                            { label: 'Permissões', icon: 'pi pi-fw pi-shield', routerLink: [raiz, U.ROOT, U.PERMISSOES], data: { breadcrumb: 'Permissões' }, permission: PERMISSOES.configuracoes.usuarios.permissoes.administrar }
                        ]
                    },
                    {
                        label: 'Comissões',
                        expanded: true,
                        icon: 'pi pi-fw pi-sitemap',
                        items: [{ label: 'Ajustes Gerais', icon: 'pi pi-fw pi-cog', routerLink: [raiz, C.COMISSOES.ROOT], data: { breadcrumb: 'Comissões' } }]
                    },
                    {
                        label: 'Dívida Ativa',
                        expanded: true,
                        icon: 'pi pi-fw pi-hammer',
                        breadcrumb: 'Dívida Ativa',
                        items: [
                            {
                                label: 'Dados Gerais',
                                icon: 'pi pi-fw pi-cog',
                                routerLink: [raiz, C.DIVIDA_ATIVA.ROOT, C.DIVIDA_ATIVA.DADOS_GERAIS],
                                data: { breadcrumb: 'Dados Gerais' },
                                permission: { modulo: 'configuracoes', funcionalidade: 'divida-ativa' } as PermissionRef
                            },
                            {
                                label: 'Entidades',
                                icon: 'pi pi-fw pi-building',
                                routerLink: [raiz, C.DIVIDA_ATIVA.ROOT, C.DIVIDA_ATIVA.ENTIDADES],
                                data: { breadcrumb: 'Entidades' },
                                permission: { modulo: 'configuracoes', funcionalidade: 'divida-ativa' } as PermissionRef
                            },
                            {
                                label: 'Documentos',
                                icon: 'pi pi-fw pi-file',
                                routerLink: [raiz, C.DIVIDA_ATIVA.ROOT, C.DIVIDA_ATIVA.DOCUMENTOS],
                                data: { breadcrumb: 'Documentos' },
                                permission: { modulo: 'configuracoes', funcionalidade: 'divida-ativa' } as PermissionRef
                            },
                            {
                                label: 'Assinantes',
                                icon: 'pi pi-fw pi-pencil',
                                routerLink: [raiz, C.DIVIDA_ATIVA.ROOT, C.DIVIDA_ATIVA.ASSINANTES],
                                data: { breadcrumb: 'Assinantes' },
                                permission: { modulo: 'configuracoes', funcionalidade: 'divida-ativa' } as PermissionRef
                            }
                        ]
                    }
                ]
            }
        ];
    }

    ngOnInit(): void {
        this.menuService.setMenu(this.menu);
        this.menuService.setSistema('SICOM | Sistema');
    }
}
